<template>
    <div>
        <div
            v-for="annotation of filteredAnnotations"
            :key="annotation._id"
            :class="generateAnnotationDotContainerClasses(annotation)"
            :style="getAnnotationStyle(annotation)"
            @click="selectAnnotation(annotation)"
        >
            <transition appear name="fade">
                <QaAnnotationDot
                    is-highlighted
                    :is-selected="checkIfAnnotationIsSelected(annotation)"
                    :type="annotation.type"
                >
                    <template v-if="annotation.status === AnnotationStatus.Unresolved">
                        {{ annotation.displayIndex }}
                    </template>
                    <template v-else>
                        <Icon type="md-checkmark" />
                    </template>
                </QaAnnotationDot>
            </transition>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import type { QaAnnotation } from "@/types/types";
import QaAnnotationDot from "@/components/Qa/QaAnnotationDot.vue";
import { AnnotationStatus } from "@/enums/annotations";

export default defineComponent({
    components: {
        QaAnnotationDot
    },
    props: {
        annotations: {
            type: Array as PropType<QaAnnotation[]>,
            required: true
        },

        selectedAnnotation: {
            type: [Object, null] as PropType<QaAnnotation | null>
        },

        scalingFactor: {
            type: Number,
            required: true
        },

        showResolved: {
            type: Boolean,
            default: true
        }
    },

    emits: ["annotationSelected"],

    data() {
        return {
            AnnotationStatus
        };
    },

    computed: {
        filteredAnnotations() {
            if (this.showResolved) {
                return this.annotations;
            }

            return this.annotations.filter(annotation => annotation.status === AnnotationStatus.Unresolved);
        }
    },

    methods: {
        checkIfAnnotationIsSelected(annotation: QaAnnotation) {
            return annotation._id === this.selectedAnnotation?._id;
        },

        generateAnnotationDotContainerClasses(annotation: QaAnnotation) {
            const classes = ["qa-banner-annotations__annotation-dot-container"];

            if (this.checkIfAnnotationIsSelected(annotation)) {
                classes.push("qa-banner-annotations__annotation-dot-container--selected");
            }

            return classes;
        },

        getAnnotationStyle(annotation: QaAnnotation) {
            return {
                left: `${annotation.xpos * this.scalingFactor}px`,
                top: `${annotation.ypos * this.scalingFactor}px`
            };
        },
        selectAnnotation(annotation: QaAnnotation) {
            this.$emit("annotationSelected", annotation);
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.qa-banner-annotations__annotation-dot-container {
    cursor: pointer;
    opacity: 0.8;
    position: absolute;
    transform: translate(-50%, -50%);
    transition: opacity 0.2s linear;
    z-index: 1;

    &:hover {
        opacity: 1;
        z-index: 2;
    }
}

.qa-banner-annotations__annotation-dot-container--selected {
    opacity: 1;
    z-index: 1;
}
</style>
